import React, { Suspense } from 'react';

export enum TourIconName {
  CANCEL = 'cancel',
  CHECKMARK = 'checkmark',
  CHEVRON_DOWN = 'chevron-down',
  DOCUMENT = 'document',
  DOLLAR = 'dollar',
  LOADING_SPINNER = 'loading-spinner',
  MENU_BUTTON = 'menu-button',
  NAVBAR_DOCS = 'navbar-docs',
  NAVBAR_DOCUMENT_SCAN = 'navbar-document-scan',
  NAVBAR_HAMBURGER = 'navbar-hamburger',
  NAVBAR_MESSAGES = 'navbar-messages',
  NAVBAR_NOTIFICATION = 'navbar-notification',
  NAVBAR_PRINTER = 'navbar-printer',
  NAVBAR_USER = 'navbar-user',
  QUESTION_MARK_CIRCLE = 'question-mark-circle',
  QUICK_TIPS_INFO = 'quick-tips-info',
  SEND = 'send',
  SUCCESS_CHECKMARK = 'success-checkmark',
  TOUR_COMPLETE_CHECKMARK = 'tour-complete-checkmark',
  WRENCH = 'wrench',
}

type IconModule = { default: React.ComponentType };
type IconImporter = () => Promise<IconModule>;

const ICON_IMPORTERS = import.meta.glob('./*.tsx') as Record<
  string,
  IconImporter
>;

const lazyIconCache: Record<
  string,
  React.LazyExoticComponent<React.ComponentType> | undefined
> = {};

const iconNameToPascalCase = (iconName: string) => {
  return iconName
    .split('-')
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join('');
};

const getIconImporter = (iconName: string): IconImporter | undefined => {
  const pascalCaseFileName = iconNameToPascalCase(iconName);
  return ICON_IMPORTERS[`./${pascalCaseFileName}.tsx`];
};

const getLazyIcon = (
  iconName: string
): React.LazyExoticComponent<React.ComponentType> | null => {
  const cached = lazyIconCache[iconName];
  if (cached) return cached;

  const importer = getIconImporter(iconName);
  if (!importer) return null;

  const LazyIcon = React.lazy(importer);
  lazyIconCache[iconName] = LazyIcon;
  return LazyIcon;
};

interface TourIconProps {
  iconName: string;
}

const TourIcon = ({ iconName }: TourIconProps) => {
  const LazyIcon = getLazyIcon(iconName);
  if (!LazyIcon) return null;

  return (
    <Suspense fallback={null}>
      <LazyIcon />
    </Suspense>
  );
};

export const getIconByName = (iconName: string) => {
  if (!getIconImporter(iconName)) return null;
  return <TourIcon iconName={iconName} />;
};

export const CancelIcon = () => getIconByName(TourIconName.CANCEL);
export const CheckmarkIcon = () => getIconByName(TourIconName.CHECKMARK);
export const ChevronDownIcon = () => getIconByName(TourIconName.CHEVRON_DOWN);
export const DocumentIcon = () => getIconByName(TourIconName.DOCUMENT);
export const DollarIcon = () => getIconByName(TourIconName.DOLLAR);
export const LoadingSpinnerIcon = () =>
  getIconByName(TourIconName.LOADING_SPINNER);
export const MenuButtonIcon = () => getIconByName(TourIconName.MENU_BUTTON);
export const NavbarDocsIcon = () => getIconByName(TourIconName.NAVBAR_DOCS);
export const NavbarDocumentScanIcon = () =>
  getIconByName(TourIconName.NAVBAR_DOCUMENT_SCAN);
export const NavbarHamburgerIcon = () =>
  getIconByName(TourIconName.NAVBAR_HAMBURGER);
export const NavbarMessagesIcon = () =>
  getIconByName(TourIconName.NAVBAR_MESSAGES);
export const NavbarNotificationIcon = () =>
  getIconByName(TourIconName.NAVBAR_NOTIFICATION);
export const NavbarPrinterIcon = () =>
  getIconByName(TourIconName.NAVBAR_PRINTER);
export const NavbarUserIcon = () => getIconByName(TourIconName.NAVBAR_USER);
export const QuestionMarkCircleIcon = () =>
  getIconByName(TourIconName.QUESTION_MARK_CIRCLE);
export const QuickTipsInfoIcon = () =>
  getIconByName(TourIconName.QUICK_TIPS_INFO);
export const SendIcon = () => getIconByName(TourIconName.SEND);
export const SuccessCheckmarkIcon = () =>
  getIconByName(TourIconName.SUCCESS_CHECKMARK);
export const TourCompleteCheckmarkIcon = () =>
  getIconByName(TourIconName.TOUR_COMPLETE_CHECKMARK);
export const WrenchIcon = () => getIconByName(TourIconName.WRENCH);
